<template>
  <div>
    <div
      class="grid lg:grid-cols-12 md:grid-cols-8 grid-cols-1 gap-4 w-full items-center py-4 my-0"
    >
      <div class="lg:col-span-2 md:col-span-2 col-span-12 flex flex-row md:flex-row">
        <span class="text-left font-bold md:hidden mr-2">Nome:</span>
        <p class="md:text-left truncate">{{ data.name }}</p>
      </div>

      <div class="lg:col-span-1 md:col-span-1 col-span-6 flex flex-row">
        <span class="text-left font-bold md:hidden mr-2">CRM:</span>
        <p>{{ data.crm }}</p>
      </div>

      <div class="lg:col-span-1 md:col-span-1 col-span-6 flex flex-row">
        <span class="text-left font-bold md:hidden mr-2">UF:</span>
        <p>{{ data.crmUf }}</p>
      </div>

      <div class="lg:col-span-1 md:col-span-1 col-span-6 flex flex-row">
        <span class="text-left font-bold md:hidden mr-2">Situação:</span>
        <p>{{ data.status }}</p>
      </div>

      <div class="lg:col-span-1 md:col-span-1 col-span-12 flex flex-row">
        <span class="text-left font-bold md:hidden mr-2">Inserido em:</span>
        <p>{{ data.insertedAt }}</p>
      </div>

      <div class="lg:col-span-2 md:col-span-1 col-span-6 flex flex-row">
        <span class="text-left font-bold md:hidden mr-2">Por:</span>
        <UserDisplay :name="data.insertedBy" />
      </div>

      <div class="lg:col-span-1 col-span-12 hidden lg:flex flex-row">
        <p>{{ data.promotedAt || ' ' }}</p>
      </div>

      <div class="lg:col-span-2 col-span-6 hidden lg:flex flex-row">
        <template v-if="data.promotedBy">
          <UserDisplay :name="data.promotedBy" />
        </template>
        <template v-else><span>&nbsp;</span></template>
      </div>

      <div
        class="lg:col-span-1 md:col-span-1 col-span-12 flex flex-row justify-start gap-4 mt-4 md:mt-0"
      >
        <v-icon
          @click="openModal('promote', data.id, data.name)"
          class="share-icon hover:cursor-pointer"
          color="primary"
          :icon="mdiChevronDoubleUp"
        />

        <v-icon
          @click="openModal('delete', data.id, data.name)"
          class="share-icon hover:cursor-pointer"
          color="negative"
          :icon="mdiPlaylistRemove"
        />
      </div>
    </div>
  </div>

  <ConfirmAlert
    :key="modalKey"
    :showAlert="showModal"
    :title="modalTitle"
    :message="modalDescription"
    :isLoading="isLoading"
    @onCancel="showModal = false"
    @onConfirm="handleConfirm"
  />
</template>

<script setup lang="ts">
import { ref } from 'vue'

import { mdiPlaylistRemove, mdiChevronDoubleUp } from '@mdi/js'
import { useDsPolicyStore } from '@/pinia-store/useDsPolicyStore'
import { useErrorToast } from '@/composables/useErrorToast'

import UserDisplay from '../AvatarUser/index.vue'
import ConfirmAlert from '@/components/designSystem/Alerts/ConfirmAlert.vue'

const policyStore = useDsPolicyStore()
const { notifyError } = useErrorToast()

const showModal = ref(false)
const modalKey = ref(0)
const isLoading = ref(false)
const modalType = ref<'delete' | 'promote'>('delete')
const selectedDoctor = ref<{ id: number; name: string }>({ id: 0, name: '' })

const modalTitle = ref('')
const modalDescription = ref('')

const openModal = (type: 'delete' | 'promote', id: number, name: string) => {
  modalType.value = type
  selectedDoctor.value = { id, name }
  showModal.value = false
  modalKey.value++
  showModal.value = true

  if (type === 'delete') {
    modalTitle.value = 'Confirmar a remoção'
    modalDescription.value = `Você tem certeza que deseja remover ${name} da política?`
  } else {
    modalTitle.value = 'Confirmar a promoção'
    modalDescription.value = `Você tem certeza que deseja promover ${name}?`
  }
}

const handleConfirm = async () => {
  if (!selectedDoctor.value.id) {
    console.error('Nenhum médico selecionado')
    return
  }

  isLoading.value = true

  try {
    if (modalType.value === 'delete') {
      await policyStore.deleteAuthorizedDoctor(selectedDoctor.value.id)
    } else if (modalType.value === 'promote') {
      await policyStore.promoteAuthorizedDoctor(selectedDoctor.value.id)
    }
  } catch (error) {
    notifyError(
      error,
      modalType.value === 'delete'
        ? 'Não foi possível remover o médico da política de restrição.'
        : 'Não foi possível promover o médico na política de restrição.'
    )
    isLoading.value = false
    return
  }

  showModal.value = false
  modalKey.value++

  try {
    const policyId = policyStore.currentPolicy?.id
    if (policyId) await policyStore.fetchAuthorizedDoctorsByDsPolicy(policyId)
  } catch (error) {
    notifyError(error, 'Não foi possível atualizar a lista de médicos autorizados.')
  } finally {
    isLoading.value = false
  }
}

interface AuthorizedDoctorDisplayData {
  id: number
  name: string
  crm: string
  crmUf: string
  status: string
  insertedAt: string
  insertedBy: string
  promotedAt: string
  promotedBy: string
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const props = defineProps<{
  data: AuthorizedDoctorDisplayData
}>()
</script>
